import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  untracked,
} from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';
import { PanelHostService } from '../../../../services/panel-host.service';
import { TripFinanceService } from '../../../../services/trip-finance.service';
import { TripService } from '../../../../services/trip.service';
import { DonationLinkInput, NewDonationWithAssignments } from '../../../../models/finance-models';
import { ParticipantAssignmentEditorComponent } from '../../../trip-finances/participant-assignment-editor/participant-assignment-editor.component';
import { getTripRoster } from '../../../trip-finances/trip-roster.util';
import { ContentContainerComponent } from '../../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../../ui/panel-body/panel-body.component';

interface OnlineDonationSnapshot {
  assignEnabled: number;
  links: DonationLinkInput[];
}

function donationLinksEqual(a: DonationLinkInput[], b: DonationLinkInput[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((link, index) => {
    const other = b[index];
    return link.userId === other.userId && link.amount === other.amount;
  });
}

function onlineDonationSnapshotsEqual(a: OnlineDonationSnapshot, b: OnlineDonationSnapshot): boolean {
  return a.assignEnabled === b.assignEnabled && donationLinksEqual(a.links, b.links);
}

@Component({
  selector: 'app-edit-online-donation-panel',
  imports: [CurrencyPipe, DatePipe, TranslocoDirective, ParticipantAssignmentEditorComponent, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './edit-online-donation-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditOnlineDonationPanelComponent implements OnDestroy {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly tripService = inject(TripService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  record = signal<NewDonationWithAssignments | null>(null);
  assignEnabled = signal(0);
  links = signal<DonationLinkInput[]>([]);
  submitting = signal(false);
  private readonly initialSnapshot = signal<OnlineDonationSnapshot | null>(null);

  roster = computed(() => getTripRoster(this.tripService.currentTrip.value()));

  assignmentValid = computed(() => {
    if (!this.assignEnabled()) {
      return true;
    }
    const total = this.record()?.amount ?? 0;
    const assigned = this.links().reduce((s, l) => s + l.amount, 0);
    return Math.abs(total - assigned) < 0.01;
  });

  constructor() {
    effect(() => {
      const panel = this.panelHost.activePanel();
      const context = this.panelHost.tripFinancePanelContext();
      if (panel === 'edit-online-donation' && context?.mode === 'online-donation' && context.record) {
        const donation = context.record as NewDonationWithAssignments;
        this.record.set(donation);
        this.assignEnabled.set(donation.assignDonation ?? 0);
        this.links.set(donation.links.map((l) => ({ userId: l.userId, amount: l.amount })));
        // Do not track links/assignEnabled — otherwise assignment edits re-run this
        // effect and reset the form from context.record.
        untracked(() => this.syncDirtyBaseline());
      }
    });

    effect(() => {
      const panel = this.panelHost.activePanel();
      const context = this.panelHost.tripFinancePanelContext();
      if (panel !== 'edit-online-donation' || context?.mode !== 'online-donation') {
        return;
      }
      this.panelHost.registerDirtyPredicate(() => this.isDirty());
    });
  }

  ngOnDestroy(): void {
    this.panelHost.clearDirtyPredicate();
  }

  cancel(): void {
    void this.panelHost.dismiss();
  }

  save(): void {
    if (this.submitting() || !this.assignmentValid()) {
      return;
    }

    const trip = this.tripService.currentTrip.value();
    const donation = this.record();
    if (!trip || !donation) {
      return;
    }

    this.submitting.set(true);
    this.tripFinanceService
      .updateOnlineDonationAssignments(trip.id, donation.id, {
        assignDonation: this.assignEnabled(),
        links: this.links(),
      })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.panels.online-donation.save-success')),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.syncDirtyBaseline();
          void this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.panels.save-error')),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  private captureSnapshot(): OnlineDonationSnapshot {
    return {
      assignEnabled: this.assignEnabled(),
      links: this.links().map((link) => ({ userId: link.userId, amount: link.amount })),
    };
  }

  private isDirty(): boolean {
    const baseline = this.initialSnapshot();
    if (!baseline) {
      return false;
    }
    return !onlineDonationSnapshotsEqual(this.captureSnapshot(), baseline);
  }

  private syncDirtyBaseline(): void {
    this.initialSnapshot.set(this.captureSnapshot());
  }
}
